import { type } from "@/api/arktype";

export const MANUAL_PROMPT_VERSION = "2";

const consultationKind = type.enumerated("online", "presential");

const manualCover = type({
	name: "string > 0",
	slug: "string",
	consultation: {
		kind: consultationKind,
		date: "string",
	},
});

export type ManualCover = typeof manualCover.infer;
export type ConsultationKind = ManualCover["consultation"]["kind"];

const labeled = type({ name: "string", description: "string" });

const aspectRow = type({ aspect: "string", info: "string" });
const titledText = type({ title: "string", text: "string[]" });
const areaText = type({ area: "string", text: "string[]" });
const procedure = type({
	title: "string",
	paragraphs: type("string[]").describe(
		"2-3 flowing paragraphs; the shape itself blocks micro-itemization.",
	),
});
const step = type({
	title: "string",
	description: "string",
});

const manualShape = {
	letter: {
		paragraphs: type("string > 0")
			.array()
			.describe("3-5 personal paragraphs in the doctor first-person voice."),
		card_phrase: type("string").describe("Rose card phrase that closes the letter."),
	},
	summary: {
		paragraph: type("string[]").describe(
			"Executive summary with the main complaint already condensed.",
		),
		indicated: labeled
			.describe("What was indicated; the rationale lives in each description.")
			.array(),
	},
	complaints: type({
		paraphrase: type("string").describe(
			"Paraphrase interpreted by the doctor, never a literal transcript quote.",
		),
		doctor_comment: type("string[]").describe(
			"1-2 paragraphs from the doctor validating the perception.",
		),
	}).array(),
	assessment: {
		general: aspectRow.array(),
		health_history: titledText.array(),
		physical: areaText.array(),
	},
	plan: {
		intro: "string[]",
		procedures: procedure.array(),
		discarded_note: type("string[] | null").describe(
			"Deferred areas with the reason; null when there is none.",
		),
		final_note: type("string[]").describe(
			"The decision-at-the-presential-consultation note; always present.",
		),
	},
	safety: {
		protocol: type("string[]").describe(
			"Cardiology assessment, hospital with ICU, thrombosis, seroma.",
		),
		pre_op: type("string[]").describe("Exams by surgery type; no physical activity."),
		recovery: type("string[]").describe("Standard timelines plus real-life personalizations."),
	},
	next_steps: step
		.describe(
			"4-6 rose cards; titles without numbering (rendering numbers them); what the patient already delivered is not a step.",
		)
		.array(),
	risks: {
		general: type("string[]").describe("Infection, bleeding, anesthetic reaction, thrombosis."),
		specific: type("string[]").describe(
			"Per procedure: seroma, residual asymmetry, sensitivity, scarring, touch-up.",
		),
	},
	investment: {
		composition: type("string[]").describe(
			"Separate bullets: medical fees, anesthetist, scrub nurse, hospital, implants. No numeric values.",
		),
		payment_note: type("string[]").describe("Free-text payment distinctions."),
		bonus: type("string[]").describe("The doctor bonus, always itemized."),
		not_included: type("string[]").describe(
			"What is NOT included: drainages plus post-op medication.",
		),
		hospitals: {
			text: type("string[]").describe(
				"Generic text when no hospital was cited in the consultation.",
			),
			table: labeled.describe("Lean table; empty when there is none.").array(),
		},
		payment: type("string[]").describe(
			"Payment forms; on mobile they fold into the composition list.",
		),
		closing: type("string[]").describe(
			"Contextual card about the quote already delivered or to be sent.",
		),
	},
	journey: {
		paragraph: type("string[]").describe(
			"Paragraph with the patient name plus the doctor full contact.",
		),
		inspirational_phrase: type("string").describe("An impactful, specific phrase, never generic."),
	},
} as const;

export const ManualSchemas = {
	cover: manualCover,
	content: type(manualShape).onUndeclaredKey("delete"),
};

export const MANUAL_CONTENT_KEYS = Object.keys(manualShape);

export type ManualContent = typeof ManualSchemas.content.infer;

export function validateManualContent(content: unknown) {
	return ManualSchemas.content.assert(content);
}

export function validateManualCover(cover: unknown) {
	return ManualSchemas.cover.assert(cover);
}
